import { useEffect, useMemo, useRef, useState } from "react";
import { shuffleArray } from "../utils/shuffle";
import type { Card } from "../types";

const ICONS = [
  "🍎", "🍌", "🍒", "🍇", "🍉", "🍋", "🍍", "🥝",
  "🍑", "🥥", "🥑", "🌶️", "🥕", "🍆", "🥔", "🍪",
  "🍰", "🧁", "🍩", "🍨", "🍫", "🍭", "🍬", "🍯",
  "☕", "🍵", "🧃", "🥤", "🍹", "🍺", "🍷", "🍸",
];

function buildDeck(rows: number, cols: number): Card[] {
  const cells = rows * cols;
  if (cells % 2 !== 0) throw new Error("Grid must have an even number of cells");
  const pairs = Math.floor(cells / 2);
  const faces = shuffleArray(ICONS).slice(0, pairs);
  const deck: Card[] = [];
  let id = 0;
  faces.forEach(face => {
    deck.push({ id: id++, face, matched: false, flipped: false });
    deck.push({ id: id++, face, matched: false, flipped: false });
  });
  return shuffleArray(deck);
}

/** Observe container size so the grid always fits on small screens. */
function useElementSize<T extends HTMLElement>() {
  const ref = useRef<T | null>(null);
  const [size, setSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const r = entries[0]?.contentRect;
      if (r) setSize({ width: r.width, height: r.height });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return { ref, size };
}

export function MemoryBoard({
  imageUrl,              // clear image (background)
  blurredUrl,            // optional (we blur via CSS)
  levelIndex,
  totalLevels,
  rows,
  cols,
  timeLimitSec,
  onComplete,            // auto-fired on last match
  onNextLevel,           // "Next level"/"Finish" button
  initialFinished = false,

  // Time extensions
  externalAddedSeconds = 0,          // total +seconds granted for THIS level
  availableExtraSec = 0,             // user's time wallet balance
  onRequestExtra,                    // ask parent to grant N seconds (e.g. 60)

  // Super hints (half-solve)
  availableHints = 0,
  onUseHint,                         // should return true if hint can be used; false triggers Shop in parent

  // ⏸️ New: pause flag from parent (Shop/Payment open)
  paused = false,
}: {
  imageUrl: string;
  blurredUrl?: string;
  levelIndex: number;
  totalLevels: number;
  rows: number;
  cols: number;
  timeLimitSec: number | null;
  onComplete: () => void;
  onNextLevel?: () => void;
  initialFinished?: boolean;
  externalAddedSeconds?: number;
  availableExtraSec?: number;
  onRequestExtra?: (sec: number) => void;

  availableHints?: number;
  onUseHint?: () => boolean;

  paused?: boolean;
}) {
  const [deck, setDeck] = useState<Card[]>(() => buildDeck(rows, cols));
  const [busy, setBusy] = useState(false);
  const [matches, setMatches] = useState(0);
  const [finished, setFinished] = useState(initialFinished);
  const [lost, setLost] = useState(false);
  const [started, setStarted] = useState(false);
  const totalPairs = (rows * cols) / 2;

  // ===== Timer state =====
  const [timeLeft, setTimeLeft] = useState<number | null>(
    timeLimitSec != null ? timeLimitSec : null
  );
  const endAtRef = useRef<number | null>(null);
  const timerRef = useRef<number | null>(null);
  const lastAppliedExtRef = useRef<number>(0); // how many secs we've applied so far

  // stable callbacks
  const onCompleteRef = useRef(onComplete);
  const onNextRef = useRef(onNextLevel);
  useEffect(() => { onCompleteRef.current = onComplete; }, [onComplete]);
  useEffect(() => { onNextRef.current = onNextLevel; }, [onNextLevel]);

  /**
   * Reset board ONLY when the level/layout/image changes.
   * IMPORTANT: externalAddedSeconds is INTENTIONALLY NOT a dependency,
   * so adding time mid-round does NOT reset the game.
   */
  useEffect(() => {
    setDeck(buildDeck(rows, cols));
    setBusy(false);
    setMatches(0);
    setFinished(initialFinished);
    setLost(false);
    setStarted(false);

    if (timerRef.current) window.clearInterval(timerRef.current);
    timerRef.current = null;
    endAtRef.current = null;

    lastAppliedExtRef.current = 0;              // start with zero applied extra time
    setTimeLeft(timeLimitSec ?? null);          // base time; extras will be added by the next effect
  }, [rows, cols, levelIndex, imageUrl, blurredUrl, timeLimitSec, initialFinished]);

  /**
   * Apply extra seconds from parent WITHOUT resetting the board.
   * We compute the delta versus what we've already applied and extend
   * the countdown endpoint in-place (or just bump shown time if not started).
   */
  useEffect(() => {
    if (timeLimitSec == null) return;
    const total = externalAddedSeconds ?? 0;
    const delta = total - lastAppliedExtRef.current;
    if (delta > 0) {
      lastAppliedExtRef.current = total;
      if (endAtRef.current) {
        endAtRef.current += delta * 1000;      // extend running timer
        setTimeLeft(t => (t == null ? null : t + delta));
      } else {
        // not started yet OR paused with no endAtRef -> reflect in displayed time
        setTimeLeft(t => (t == null ? timeLimitSec + total : t + delta));
      }
    }
  }, [externalAddedSeconds, timeLimitSec]);

  /**
   * ⏸️ When paused: freeze countdown (compute remaining and drop endAtRef so resume can seed from timeLeft).
   */
  useEffect(() => {
    if (timeLimitSec == null) return;
    if (!started || lost || finished) return;

    if (paused) {
      if (timerRef.current) window.clearInterval(timerRef.current);
      timerRef.current = null;

      if (endAtRef.current) {
        const remaining = Math.max(0, Math.ceil((endAtRef.current - Date.now()) / 1000));
        setTimeLeft(remaining);
        endAtRef.current = null; // ensure resume re-seeds from timeLeft
      }
    }
  }, [paused, timeLimitSec, started, lost, finished]);

  /**
   * Timer loop (starts on first flip). Also respects `paused`.
   * When resuming (no endAtRef), seed countdown using current `timeLeft`.
   */
  useEffect(() => {
    if (timeLimitSec == null) return;
    if (paused || !started || lost || finished) {
      if (timerRef.current) window.clearInterval(timerRef.current);
      timerRef.current = null;
      return;
    }

    if (!endAtRef.current) {
      // seed from current timeLeft if available; otherwise from full (base+extra)
      const baseSeconds =
        timeLeft != null
          ? timeLeft
          : (timeLimitSec + (lastAppliedExtRef.current ?? 0));
      endAtRef.current = Date.now() + baseSeconds * 1000;
    }

    const tick = () => {
      if (!endAtRef.current) return;
      const delta = endAtRef.current - Date.now();
      const next = Math.max(0, Math.ceil(delta / 1000));
      setTimeLeft(next);
      if (next <= 0) {
        setLost(true);
        if (timerRef.current) window.clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
    tick();
    timerRef.current = window.setInterval(tick, 250);

    return () => {
      if (timerRef.current) window.clearInterval(timerRef.current);
      timerRef.current = null;
    };
  }, [paused, started, lost, finished, timeLimitSec, timeLeft]);

  const timePct = timeLimitSec
    ? Math.max(
        0,
        Math.min(
          100,
          Math.round(
            ((timeLeft ?? timeLimitSec) /
              (timeLimitSec + lastAppliedExtRef.current)) *
              100
          )
        )
      )
    : 100;

  const progressPct = totalPairs
    ? Math.min(100, Math.max(0, (matches / totalPairs) * 100))
    : 0;

  // responsive grid sizing
  const { ref: stageRef, size: stageSize } = useElementSize<HTMLDivElement>();
  const grid = useMemo(() => {
    const inset = 16, gap = 10, minCard = 36;
    const w = Math.max(0, stageSize.width - inset * 2 - gap * (cols - 1));
    const h = Math.max(0, stageSize.height - inset * 2 - gap * (rows - 1));
    const card = Math.floor(Math.max(minCard, Math.min(w / cols, h / rows)));
    const fontSize = Math.floor(card * 0.55);
    return { inset, gap, card, fontSize };
  }, [stageSize, rows, cols]);

  // ---------------------
  // Half-solve logic
  // ---------------------
  const runHalfSolve = () => {
    setDeck(prev => {
      const d = prev.map(c => ({ ...c }));

      // Collect unmatched pairs by face
      const byFace: Record<string, number[]> = {};
      d.forEach((c, i) => {
        if (!c.matched) {
          (byFace[c.face] ??= []).push(i);
        }
      });

      const pairs: [number, number][] = [];
      Object.values(byFace).forEach((idxs) => {
        if (idxs.length === 2) pairs.push([idxs[0], idxs[1]]);
      });

      const remainingPairs = pairs.length;
      if (remainingPairs === 0) return d;

      const alreadyMatchedHalf = matches >= Math.ceil(totalPairs / 2);
      let toSolveCount = alreadyMatchedHalf ? remainingPairs : Math.ceil(remainingPairs / 2);
      toSolveCount = Math.max(1, Math.min(remainingPairs, toSolveCount));

      const chosen = shuffleArray(pairs).slice(0, toSolveCount);

      chosen.forEach(([a, b]) => {
        if (d[a]) { d[a].matched = true; d[a].flipped = true; }
        if (d[b]) { d[b].matched = true; d[b].flipped = true; }
      });

      setMatches(m => Math.min(totalPairs, m + toSolveCount));

      const allMatched = d.every(c => c.matched);
      if (allMatched && !finished) {
        setFinished(true);
        if (timerRef.current) window.clearInterval(timerRef.current);
        timerRef.current = null;
        setTimeout(() => onCompleteRef.current(), 250);
      }

      return d;
    });
  };

  const handleHalfSolveClick = () => {
    if (!onUseHint) return;
    const allowed = onUseHint(); // parent decrements balance or opens Shop
    if (!allowed) return;
    runHalfSolve();
  };

  const handleFlip = (idx: number) => {
    if (busy || lost || finished) return;
    const current = deck[idx];
    if (!current || current.flipped || current.matched) return;

    if (timeLimitSec != null && !started) setStarted(true);

    setDeck(prev => {
      const d = prev.map(c => ({ ...c }));
      const open = d.reduce<number[]>((a, c, i) => { if (c.flipped && !c.matched) a.push(i); return a; }, []);
      if (open.length >= 2) open.forEach(i => { if (!d[i].matched) d[i].flipped = false; });
      if (!d[idx].matched) d[idx].flipped = true;

      const openAfter = d.reduce<number[]>((a, c, i) => { if (c.flipped && !c.matched) a.push(i); return a; }, []);
      if (openAfter.length === 2) {
        setBusy(true);
        const [a, b] = openAfter;
        setTimeout(() => {
          setDeck(latest => {
            const u = latest.map(c => ({ ...c }));
            const isMatch = u[a]?.face && u[b]?.face && u[a].face === u[b].face;
            if (isMatch) {
              if (u[a]) u[a].matched = true;
              if (u[b]) u[b].matched = true;
              setMatches(m => Math.min(totalPairs, m + 1));
              const allMatched = u.every(c => c.matched);
              if (allMatched && !finished) {
                setFinished(true);
                if (timerRef.current) window.clearInterval(timerRef.current);
                timerRef.current = null;
                setTimeout(() => onCompleteRef.current(), 250);
              }
            } else {
              if (u[a] && !u[a].matched) u[a].flipped = false;
              if (u[b] && !u[b].matched) u[b].flipped = false;
            }
            return u;
          });
          setBusy(false);
        }, 400);
      }
      return d;
    });
  };

  const resetLevel = () => {
    setDeck(buildDeck(rows, cols));
    setBusy(false);
    setMatches(0);
    setFinished(initialFinished);
    setLost(false);
    setStarted(false);

    if (timerRef.current) window.clearInterval(timerRef.current);
    timerRef.current = null;
    endAtRef.current = null;
    lastAppliedExtRef.current = 0;
    setTimeLeft(timeLimitSec ?? null);
  };

  const isLast = levelIndex + 1 >= totalLevels;

  return (
    <div className="level-wrap" style={{ display: "grid", gap: 12 }}>
      {/* TOP BAR */}
      {!finished ? (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
          {/* Left cluster: time & hint */}
          <div style={{ display: "flex", gap: 8 }}>
            {timeLimitSec ? (
              <button
                className="tab-button"
                onClick={() => onRequestExtra?.(60)}
                disabled={!onRequestExtra}
                title={availableExtraSec >= 60 ? "Use +60s" : "Time"}
              >
                {availableExtraSec >= 60 ? `➕ +60s (${availableExtraSec / 60})` : "🛒 Time"}
              </button>
            ) : null}

            <button
              className="tab-button"
              onClick={handleHalfSolveClick}
              title={availableHints > 0 ? "Use Super Hint" : "Buy a Super Hint"}
            >
              {availableHints > 0 ? `✨ Hints (${availableHints})` : "🛒 Hint"}
            </button>
          </div>

          <button className="tab-button" onClick={resetLevel}>Restart</button>
        </div>
      ) : (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
            background: "rgba(0,0,0,0.35)",
            padding: "8px 12px",
            borderRadius: 12
          }}
        >
          <div style={{ fontWeight: 800 }}>Completed ⭐</div>
          <button className="agree-button" onClick={() => onNextRef.current?.()}>
            {isLast ? "Finish" : "Next →"}
          </button>
        </div>
      )}

      {/* Timer progress (only while playing and not lost) */}
      {!finished && !lost && timeLimitSec && (
        <div className="timer-track" aria-hidden="true">
          <div className="timer-fill" style={{ width: `${timePct}%` }} />
        </div>
      )}

      {/* Stage */}
      <div
        ref={stageRef}
        style={{
          position: "relative",
          borderRadius: 12,
          overflow: "hidden",
          height: "clamp(320px, 70vh, 120vw)",
          backgroundColor: "#111",
        }}
      >
        {/* background (blur while playing) */}
        <img
          src={imageUrl}
          alt="level"
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
            filter: finished ? "blur(0px)" : "blur(12px)",
            transform: finished ? "scale(1)" : "scale(1.015)",
            transition: "filter 220ms ease, transform 220ms ease",
            willChange: "filter, transform",
          }}
        />

        {/* scrim while playing */}
        {!finished && !lost && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              background: "linear-gradient(0deg, rgba(0,0,0,0.25), rgba(0,0,0,0.25))",
              pointerEvents: "none",
            }}
          />
        )}

        {/* badges */}
        {!finished && !lost && timeLimitSec && (
          <div className="timer-badge" style={{ position: "absolute", top: 8, right: 12 }}>
            ⏱️ {(timeLeft ?? timeLimitSec)}s {paused ? "⏸" : ""}
          </div>
        )}
        {!finished && !lost && (
          <div className="reveal-chip" style={{ position: "absolute", bottom: 8, right: 12 }}>
            Pairs {matches}/{totalPairs} · {Math.round(progressPct)}%
          </div>
        )}

        {/* lost overlay */}
        {lost && (
          <div className="lost-overlay" style={{ position: "absolute", inset: 0, zIndex: 3 }}>
            Time's up! Try again ☝️
          </div>
        )}

        {/* grid overlay — hides on win OR timeout */}
        <div
          style={{
            position: "absolute",
            inset: grid.inset,
            display: "grid",
            gridTemplateColumns: `repeat(${cols}, ${grid.card}px)`,
            gridAutoRows: `${grid.card}px`,
            gap: grid.gap,
            placeContent: "center",
            transition: "opacity 220ms ease",
            opacity: finished || lost ? 0 : 1,
            pointerEvents: finished || lost ? "none" : undefined,
          }}
        >
          {deck.map((c, i) => (
            <button
              key={c.id}
              onClick={() => handleFlip(i)}
              className="card"
              disabled={c.matched || lost || finished || busy || paused}
              style={{
                width: `${grid.card}px`,
                height: `${grid.card}px`,
                borderRadius: 12,
                border: "1px solid rgba(0,0,0,0.15)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: grid.fontSize,
                background: c.matched ? "#d1fae5" : c.flipped ? "#fff" : "rgba(243,244,246,0.95)",
                boxShadow: c.flipped ? "0 4px 12px rgba(0,0,0,0.12)" : "none",
                cursor: c.matched ? "default" : "pointer",
                transition: "all 150ms ease",
                opacity: paused ? 0.6 : 1,
              }}
            >
              {c.flipped || c.matched ? c.face : "❓"}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
